import { Button } from "../ui/Button"
import { Suspense, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogProps } from "@/components/ui/Dialog"
import {
	currentStyleAtom,
	RemoteStyle,
	SelectStyleOptions,
	showStylePreviewDialog,
} from "@/store/style/style-state"
import { pushDialog, replaceDialog } from "@/store/dialogs-state"
import { CreateStyleDialog, type CreateStyleSelectOptions } from "./CreateStyleDialog"
import { AIStylesContainer } from "../Styles/AIStylesContainer"
import { StyleTab } from "@/store/style/style-types"
import { getRandomPhotosForStylePreview } from "@/store/photo/photos-state"
import { StyleResponseDTO } from "@/api/dto/styles"
import { StyleService } from "@/store/style/style-service"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { ConfirmDialog } from "./ConfirmDialog"
import { useAtomValue } from "jotai"
import { PhotoId } from "@/store/undoable-state"
import { publishStyleDraft } from "@/store/style/publish-style"
import { aiStylesTabAtom } from "@/store/style/style-preferences"

export type StylesListType = "display" | "select"

export const AllStylesDialog = (
	props: DialogProps & {
		onSelect: (options: SelectStyleOptions) => void
		previewAvoidingIds?: PhotoId[]
	},
) => {
	const { t } = useTranslation("components")
	const [selectedStyle, setSelectedStyle] = useState<StyleResponseDTO | undefined>(undefined)
	const currentStyle = useAtomValue(currentStyleAtom)
	const tab = useAtomValue(aiStylesTabAtom)

	const [isDetailView, setIsDetailView] = useState(false)
	const [isLoadingStyle, setIsLoadingStyle] = useState(false)
	const isPixiesetAuth = isPixiesetAuthEnabled()

	const confirmSelectedStyle = async (style?: StyleResponseDTO) => {
		style = style ?? selectedStyle
		if (!style) {
			return
		}
		// Show inline loading state immediately for better UX
		setIsLoadingStyle(true)

		try {
			const references = await StyleService.getReferencesForStyle(style.styleId)
			const remoteStyle = new RemoteStyle(style, references)
			const previewPhotos = getRandomPhotosForStylePreview(props.previewAvoidingIds)

			// i18n-ignore: projectStyleType is an analytics/tracking identifier
			const projectStyleType =
				tab === "polarr"
					? isPixiesetAuth
						? "Pixieset AI Style" // i18n-ignore
						: "Polarr AI Style" // i18n-ignore
					: "My AI Style" // i18n-ignore

			showStylePreviewDialog(props, remoteStyle, projectStyleType, previewPhotos, (style) => {
				props.onSelect({ style, needsSetup: false })
			})

			// Clear loading state after dialog is shown
			setIsLoadingStyle(false)
		} catch (error) {
			console.error("Error loading style:", error)
			setIsLoadingStyle(false)
			replaceDialog(ConfirmDialog, {
				title: t("stylesListDialog.failedToLoadTitle"),
				message: t("stylesListDialog.failedToLoadMessage"),
				primary: { title: t("common:action.confirm") },
				trackingViewName: "load_style_failure_dialog",
			})
		}
	}

	const createNewStyle = () => {
		pushDialog(CreateStyleDialog, {
			...props,
			onSelect: async (options) => {
				props.onSelect(await toSelectStyleOptions(options))
			},
		})
	}

	const toSelectStyleOptions = async (
		options: CreateStyleSelectOptions,
	): Promise<SelectStyleOptions> => {
		if ("styleDraft" in options) {
			const publishResult = await publishStyleDraft(options.styleDraft)
			return {
				style: publishResult.styleInfo,
				needsSetup: false,
				publishedReferences: publishResult.references,
			}
		}

		return {
			style: "new",
			needsSetup: true,
			isExpressSetup: options.isExpressSetup,
		}
	}

	const handleUseStyle = (style: StyleResponseDTO) => {
		void confirmSelectedStyle(style)
	}

	const isCurrentStyle = !selectedStyle
		? false
		: selectedStyle.styleId === currentStyle?.styleId
			? true
			: false

	const renderContinueButton = () => {
		return (
			<Button
				trackingLabel="continue"
				trackingValue={selectedStyle?.name}
				variant={isCurrentStyle ? "default" : "primary"}
				disabled={!selectedStyle || isCurrentStyle || isLoadingStyle}
				onClick={() => void confirmSelectedStyle()}
			>
				{isLoadingStyle
					? t("stylesListDialog.loading")
					: isCurrentStyle
						? t("stylesListDialog.aiStyleInUse")
						: t("common:action.continue")}
			</Button>
		)
	}

	const bottomButtons = () => {
		return (
			<div className="flex w-full flex-row justify-end space-x-4">
				{renderContinueButton()}
			</div>
		)
	}

	return (
		<Dialog
			{...props}
			disableClose={isDetailView || props.disableClose}
			canGoBack={!isDetailView ? props.canGoBack : false}
			bottomButtons={isDetailView ? undefined : bottomButtons()}
			contentSize="lg"
			trackingViewName={"all_styles_dialog"}
		>
			<Suspense>
				<AIStylesContainer
					onSelect={(style, skipConfirm) => {
						if (skipConfirm) {
							void confirmSelectedStyle(style)
						} else {
							setSelectedStyle(style)
						}
					}}
					selectedStyle={selectedStyle}
					onAddNew={createNewStyle}
					onUse={handleUseStyle}
					isLoadingUse={isLoadingStyle}
					onDetailViewChange={(
						detailViewShown: boolean,
						_style?: StyleResponseDTO,
						_tab?: StyleTab,
					) => {
						setIsDetailView(detailViewShown)
					}}
				/>
			</Suspense>
		</Dialog>
	)
}
